import { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import type { WishlistItem } from '../../types';
import { Button } from '../ui/button';
import { Input, FormField, Label } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import {
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogCloseButton,
  DialogFooter,
} from '../ui/dialog';

interface WishlistDialogProps {
  open: boolean;
  editingItem: WishlistItem | null;
  onSubmit: (data: Omit<WishlistItem, 'id' | 'createdAt' | 'addedBy' | 'order'>) => Promise<void>;
  onClose: () => void;
  itemToDelete: WishlistItem | null;
  onDeleteConfirm: () => Promise<void>;
  onDeleteCancel: () => void;
}

function toInputDate(date: Date): string {
  return date.toISOString().split('T')[0]!;
}

export function WishlistDialog({
  open,
  editingItem,
  onSubmit,
  onClose,
  itemToDelete,
  onDeleteConfirm,
  onDeleteCancel,
}: WishlistDialogProps) {
  const [title, setTitle] = useState('');
  const [links, setLinks] = useState<string[]>(['']);
  const [averagePrice, setAveragePrice] = useState(0);
  const [targetDate, setTargetDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(editingItem?.title ?? '');
      setLinks(editingItem?.links?.length ? editingItem.links : ['']);
      setAveragePrice(editingItem?.averagePrice ?? 0);
      setTargetDate(editingItem ? toInputDate(editingItem.targetDate) : '');
      setLoading(false);
    }
  }, [open, editingItem]);

  const updateLink = (index: number, value: string) =>
    setLinks((prev) => prev.map((l, i) => (i === index ? value : l)));

  const addLink = () => setLinks((prev) => [...prev, '']);

  const removeLink = (index: number) =>
    setLinks((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !targetDate) return;
    setLoading(true);
    try {
      const cleanLinks = links.map((l) => l.trim()).filter(Boolean);
      await onSubmit({
        title: title.trim(),
        links: cleanLinks.length > 0 ? cleanLinks : undefined,
        averagePrice,
        targetDate: new Date(targetDate + 'T00:00:00'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogRoot open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogPortal>
          <DialogBackdrop className="z-40" />
          <DialogPopup className="z-41">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Editar desejo' : 'Novo desejo'}</DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogContent>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <FormField>
                  <Label htmlFor="wish-title">Nome</Label>
                  <Input
                    id="wish-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ex: Tênis Nike Air Max"
                    required
                  />
                </FormField>

                <FormField>
                  <Label>Links (opcional)</Label>
                  <div className="flex flex-col gap-2">
                    {links.map((link, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input
                          value={link}
                          onChange={(e) => updateLink(i, e.target.value)}
                          placeholder="https://..."
                          type="url"
                        />
                        {links.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLink(i)}
                            className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-500"
                          >
                            <X className="size-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addLink}
                      className="flex items-center gap-1.5 self-start rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Plus className="size-3.5" />
                      Adicionar link
                    </button>
                  </div>
                </FormField>

                <FormField>
                  <Label>Preço médio</Label>
                  <CurrencyInput value={averagePrice} onChange={setAveragePrice} />
                </FormField>

                <FormField>
                  <Label htmlFor="wish-date">Data alvo para compra</Label>
                  <Input
                    id="wish-date"
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    required
                  />
                </FormField>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Salvando…' : editingItem ? 'Salvar' : 'Adicionar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>

      <DialogRoot open={!!itemToDelete} onOpenChange={(isOpen) => { if (!isOpen) onDeleteCancel(); }}>
        <DialogPortal>
          <DialogBackdrop />
          <DialogPopup className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Confirmar exclusão</DialogTitle>
              <DialogCloseButton />
            </DialogHeader>
            <DialogContent>
              <p className="text-sm text-foreground-subtle">
                Tem certeza que deseja excluir{' '}
                <strong className="text-foreground">{itemToDelete?.title}</strong> da lista de desejos?
              </p>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" className="flex-1" onClick={onDeleteCancel}>
                  Cancelar
                </Button>
                <Button variant="destructive" className="flex-1" onClick={onDeleteConfirm}>
                  Excluir
                </Button>
              </div>
            </DialogContent>
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>
    </>
  );
}
